<template>
    <div class="quick-shop"
         :data-insights-object-id="selectedVariant"
         :data-insights-query-id="queryId"
         :data-insights-product-index="variantsIndexName">
        <default-error v-if="error"/>

        <img src="@/../shared/assets/images/close.svg" class="close" @click="$emit('close')"/>

        <product-images-gallery :images="getImages"
                                :onSale="getOnSale"
                                :price="parseFloat(getPrice)"
                                :salePrice="parseFloat(getSalePrice)"
                                :viewLarge="false"/>

        <div class="product-manage">
            <div class="product-name" :class="{ 'no-transform': product.gift_card }">
                {{ getName }}
            </div>

            <div class="price">
                <div class="product-price" v-if="getOnSale">
                    <span class="current sale">
                        {{ formatPrice(getSalePrice) }}
                    </span>

                    <span class="old" aria-label="Price before sale">
                        {{ formatPrice(getPrice) }}
                    </span>

                    <h3 v-if="this.showFreeShipping" class="free-shipping">
                        <i class="fas fa-truck"/> Free Shipping
                    </h3>
                </div>

                <div class="product-price" v-else>
                    <span class="current">
                        {{ formatPrice(getPrice) }}
                    </span>

                    <h3 v-if="this.showFreeShipping" class="free-shipping">
                        <i class="fas fa-truck"/> Free Shipping
                    </h3>
                </div>
            </div>

            <div>
                <product-options v-if="Object.keys(multivariantOptions).length === 1"
                                 :options="options"
                                 :selected="selectedVariant"
                                 :show-phantoms="showPhantoms"
                                 @changeVariant="updateVariant"
                                 @loading="manageLoading">
                </product-options>

                <product-multivariant-options v-else
                                              :options="options"
                                              :multivariantOptions="multivariantOptions"
                                              :selected="selectedVariant"
                                              :show-phantoms="showPhantoms"
                                              @changeVariant="updateVariant"
                                              @loading="manageLoading"
                                              @canAddToCart="manageCanSelectVariant">
                </product-multivariant-options>

                <product-backorder v-if="showAvailability"
                                   :getBackorderable="getBackorderable"
                                   :getBackorderableDate="getBackorderableDate"
                                   :getQuantity="getVariantQuantity"
                                   :getCustomMadeInfo="getCustomMadeInfo"
                                   :selectedVariant="selectedVariant"
                                   :getMadeToOrder="getMadeToOrder"
                                   :getLeadTime="getLeadTime"/>

                <div class="product-quantity">
                    <h2>
                        <span class="label">QUANTITY:</span>

                        <div class="number-btn">

                            <div class="manage-input"
                                 :class="{ disabled: quantity <= minQuantity }"
                                 @click="submitChangeValueBy(-1)">
                                <i class="fas fa-minus"/>
                            </div>

                            <input class="amount"
                                   :value="quantity"
                                   type="number"
                                   :disabled="!canSelectVariant"
                                   @change="validateValue"/>

                            <div class="manage-input"
                                 :class="{ disabled: addQuantityDisabled }"
                                 @click="submitChangeValueBy(1)">
                                <i class="fas fa-plus"/>
                            </div>

                        </div>
                    </h2>
                </div>

                <div class="select-product">
                    <div class="btn inverted btn-select-product"
                         :class="{ disabled: !canSelectVariant }"
                         @click="addToCart">
                        ADD TO CART
                    </div>

                    <a :href="`/products/${getVariantSlug}?queryId=${queryId}`"
                       class="btn btn-select-product">
                        VIEW MORE DETAILS
                    </a>
                </div>
            </div>
        </div>

        <!-- <div v-html="fbAddToCart">{{ fbAddToCart }}</div> -->
    </div>
</template>

<script>
    import ProductImagesGallery from "@/../shared/components/ProductImagesGallery"
    import ProductOptions from "@/../shared/components/ProductOptions"
    import ProductMultivariantOptions from "@/../shared/components/ProductMultivariantOptions"
    import ApiProduct from '@/../shared/api/Product'
    import PriceMixin from '@/../shared/mixins/price'
    import SelectedVariantPayloadMixin from '@/../shared/mixins/selectedVariantPayload'
    import DefaultError from '@/components/DefaultError'
    import ApiOrder from '@/api/Order'
    import ProductBackorder from '@/components/ProductBackorder'
    import AlgoliaEventsMixin from '@/mixins/algoliaEvents'
    import { isNil, compact } from 'lodash'

    export default {
        name: "quickShop",
        components: {
            DefaultError,
            ProductMultivariantOptions,
            ProductOptions,
            ProductImagesGallery,
            ProductBackorder
        },
        mixins: [
            PriceMixin,
            SelectedVariantPayloadMixin,
            AlgoliaEventsMixin
        ],
        props: {
            product: {
                type: Object,
                required: true
            },
            showPhantoms: {
                type: Boolean,
                default: false
            },
            queryId: {
                type: String,
                default: null
            }
        },
        data () {
          return {
            loading: true,
            error: false,
            canSelectVariant: true,
            selectedVariant: '',
            images: [],
            options: {},
            variants_names: {},
            multivariantOptions: {},
            quantity: 1,
            minQuantity: 1,
            fbAddToCart: ''
          }
        },
        watch: {
            product () {
                this.loadProduct()
            }
        },
        computed: {
            getSku() {
                return this.error ? '' : this.$store.getters['productData/getSku'](this.selectedVariant)
            },
            getVariantSlug () {
                return this.$store.getters['productData/getSlug'](this.selectedVariant)
            },
            showFreeShipping() {
              const price = this.getOnSale ? this.getSalePrice : this.getPrice
              const freeShippingThreshold =
                this.getShippingCategory.match(/freight/i)
                    ? this.$freightFreeShippingTier
                    : this.$smallParcelFreeShippingTier

              return price >= freeShippingThreshold && this.getShippingCategory !== 'e_deliver'
            },
            getPrice() {
                return this.error ? '' : this.$store.getters['productData/getPrice'](this.selectedVariant)
            },
            getOnSale() {
                return this.error ? '' : this.$store.getters['productData/onSale'](this.selectedVariant)
            },
            getSalePrice() {
                return this.error ? '' : this.$store.getters['productData/getSalePrice'](this.selectedVariant)
            },
            getShippingCategory () {
                return this.error ? '' : this.$store.getters['productData/getShippingCategory'](this.selectedVariant)
            },
            maxQuantity () {
                return this.getVariantBackorderable || this.getMadeToOrder || this.getGiftCard ? null : this.getVariantQuantity
            },
            getVariantQuantity () {
                return this.$store.getters['productData/getQuantity'](this.selectedVariant)
            },
            getMetaPrice() {
                return this.error ? '' : (this.$store.getters['productData/onSale'](this.selectedVariant) ? this.$store.getters['productData/getSalePrice'](this.selectedVariant) : this.$store.getters['productData/getPrice'](this.selectedVariant))
            },
            getPinterestLineItems() {
              let value = `&ed[line_items][0][product_id]=` + this.selectedVariant +
                  `&ed[line_items][0][product_name]=` + this.getName +
                  `&ed[line_items][0][product_category]=` + this.getCategoryName +
                  `&ed[line_items][0][product_brand]=` + this.getSupplierName
              return value
            },
            getName() {
                return this.error ? '' : this.$store.getters['productData/getVariantName'](this.selectedVariant) || this.$store.getters['productData/getName']
            },
            getCategoryName() {
              return this.error ? '' : this.$store.getters['productData/getCategoryName']
            },
            getSupplierName() {
                return this.error ? '' : this.$store.getters['productData/getSupplierName']
            },
            productionEnv() {
                return process.env.RAILS_ENV === 'production'
            },
            productExistWithVariant () {
                return this.$store.getters['productData/productExistWithVariant'](this.getVariantSlug)
            },
            getVariantBackorderable () {
                return this.$store.getters['productData/getBackorderable'](this.selectedVariant)
            },
            getBackorderable() {
                return this.error ? '' : (this.getVariantBackorderable && this.getVariantQuantity == 0)
            },
            getVariantAvailability () {
                let available = this.productExistWithVariant

                if (this.getVariantBackorderable || this.getMadeToOrder || this.getGiftCard) {
                    return available
                } else {
                    return available && this.getVariantQuantity >= this.quantity && this.quantity > 0
                }
            },
            showAvailability () {
                return this.error ? false : !this.getGiftCard
            },
            getGiftCard () {
                return this.error ? false : this.$store.getters['productData/getGiftCard']
            },
            getCustomMadeInfo() {
                return this.error ? '' : this.$store.getters['productData/getCustomMadeInfo'](this.selectedVariant)
            },
            getLeadTime () {
                return this.error ? '' : this.$store.getters['productData/getLeadTime'](this.selectedVariant)
            },
            getMadeToOrder () {
                return this.error ? '' : this.$store.getters['productData/getMadeToOrder'](this.selectedVariant)
            },
            getBackorderableDate() {
                return this.error ? '' : this.$store.getters['productData/getBackorderableDate'](this.selectedVariant)
            },
            addQuantityDisabled () {
                return !this.unlimitedQuantity && this.quantity >= this.maxQuantity
            },
            unlimitedQuantity () {
                return this.maxQuantity === null || this.getVariantBackorderable || this.getMadeToOrder || this.getGiftCard
            }
        },
        mounted () {
            this.loadProduct()
        },
        methods: {
            manageCanSelectVariant (value) {
                this.canSelectVariant = value && this.getVariantAvailability
            },
            updateVariant(payload) {
                this.selectedVariant = payload['selectedVariant']
                this.images = this.$store.getters['productData/getImages'](this.selectedVariant)

                this.canSelectVariant = this.getVariantAvailability
                this.quantity = 1
            },
            manageLoading(value) {
                this.loading = value
            },
            addToCart() {
                if (!this.canSelectVariant) return

                this.fbPixelAddToCart(this.selectedVariant)
                this.sendAddToCartEventToAlgolia()

                ApiOrder.addToOrder(this.$axios, {
                    variant_id: this.selectedVariant,
                    quantity: this.quantity,
                    order_id: this.$store.getters['order/getOrderId'],
                    gclid: this.$store.getters['order/getGclid']
                })
                    .then(response => {
                        this.$store.dispatch('order/setOrderId', response.data.data.id)
                        this.$store.dispatch('order/setOrder', response.data.data)
                        this.$root.$emit('displayAddedToCart')
                        this.loading = false
                        this.quantity = 1
                        this.canSelectVariant = this.getVariantAvailability
                    })
                    .catch(error => {
                        console.log('error')
                        this.loading = false
                    })
            },
            fbPixelAddToCart(variant_id) {
                if (this.productionEnv) {
                    this.fbAddToCart = `<img height="1" width="1" style="display:none"
                    src="https://www.facebook.com/tr?id=527328117436744&ev=AddToCart&cd[content_type]=product&cd[content_ids]=${variant_id}&cd[currency]=USD&cd[value]=${this.getMetaPrice}&noscript=1"/>
                    <img height="1" width="1" style="display:none"
                        src="https://ct.pinterest.com/v3/?tid=2620960805142&event=AddToCart&ed[value]=${this.getMetaPrice}&ed[order_quantity]=${this.quantity}&${this.getPinterestLineItems}&noscript=1"/>`
                }
            },
            setProductData() {
                this.selectedVariant = this.$store.getters['productData/getMainVariantId']
                this.options = this.$store.getters['productData/getOptions']()
                this.variants_names = this.$store.getters['productData/getVariantsNames']
                this.multivariantOptions = this.$store.getters['productData/getMultivariantOptions']()
                this.images = this.$store.getters['productData/getImages'](this.selectedVariant)
            },
            loadProduct () {
                let checkForProduct = this.$store.getters['productData/productExistWithVariant'](this.product.slug, this.product.objectID)

                if (checkForProduct) {
                    console.log("PRODUCT - FETCH FROM STORE")
                    this.$store.dispatch('productData/setCurrentProduct', this.product.slug)
                    this.setProductData()

                    this.quantity = 1
                    this.canSelectVariant = this.getVariantAvailability

                    this.loading = false
                } else {
                    console.log("PRODUCT - FETCH FROM API")
                    ApiProduct.getProduct(this.$axios, this.product.slug, this.getQueryParams())
                        .then(response => {
                            this.setProductFromResponse(response)
                            this.loading = false
                        })
                        .catch(error => {
                            if (error?.response?.status === 410) {
                                this.setProductFromResponse(error.response)
                            } else {
                                console.log(error)
                                this.error = true
                            }

                            this.loading = false
                        })
                }
            },
            setProductFromResponse (response) {
                this.$store.dispatch('productData/initProduct', response.data.data)
                this.setProductData()

                this.quantity = 1
                this.canSelectVariant = this.getVariantAvailability
            },
            getQueryParams () {
                return {
                    variant_id: this.product.objectID,
                    show_phantoms: this.showPhantoms
                }
            },
            submitChangeValueBy (quantity) {
                const newValue = this.quantity + quantity

                if (isNil(this.quantity)) {
                  this.quantity = 0
                } else if (
                    quantity > 0 && (this.unlimitedQuantity || newValue <= this.maxQuantity) ||
                    quantity < 0 && newValue >= this.minQuantity
                ) {
                  this.quantity = newValue
                }
            },
            validateValue ($event) {
                const newValue = compact([this.minQuantity, +$event.target.value, this.maxQuantity]).sort((a, b) => a - b)[1]

                this.quantity = null // refresh input
                this.quantity = newValue
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../assets/stylesheets/variables.scss';
    @import '../../assets/stylesheets/animations/fade';


    .quick-shop {
        border-top: 1px solid #cccccc;
        border-bottom: 1px solid #cccccc;
        padding-top: 25px;
        padding-bottom: 25px;
        position: relative;

        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 20px;
        grid-auto-rows: min-content;

        .close {
            position: absolute;
            top: 25px;
            right: 0;
            font-size: 2.4rem;
            color: #2e2e2e;
            cursor: pointer;

            @media only screen and (max-width: 992px) {
                top: 10px;
            }
        }

        //LEFT COLUMN
        .product-images-gallery {
            grid-row: 1;
            grid-column: 1;
            grid-auto-rows: min-content;
        }

        // RIGHT COLUMN
        .product-manage {
            display: grid;
            grid-row-gap: 20px;
            grid-row: 1/2;
            grid-auto-rows: min-content;

            .product-name {
                font-family: Tenez;
                font-size: 3.4rem;
                font-weight: normal;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.2;
                letter-spacing: 0.7px;
                text-align: left;
                color: $color-black;
                text-transform: capitalize;
                padding-right: 35px;
            }

            .price {
                display: grid;
                grid-template-columns: 1fr;

                .product-price {
                    display: grid;
                    grid-template-columns: repeat(3, auto) 1fr;
                    grid-column-gap: 17px;
                    grid-row-gap: 6px;
                    grid-auto-columns: min-content;
                    justify-items: start;
                    align-items: center;

                    font-style: normal;
                    font-stretch: normal;
                    line-height: 1.33;
                    letter-spacing: 0.5px;
                    text-align: left;
                    color: #989797;
                    font-size: 1.6rem;
                    font-weight: bold;

                    .current {
                        font-size: 1.6rem;
                        color: #2e2e2e;
                        font-weight: normal;
                    }

                    .sale {
                        color: $color-sale;
                    }

                    .old {
                        font-family: 'OpenSans Light';
                        padding-top: 3px;
                        line-height: 22px;
                        color: #989797;
                        text-decoration: line-through;
                    }

                    @media only screen and (max-width: 768px) {
                        grid-template-columns: repeat(3, auto);
                        grid-column-gap: 12px;
                    }

                }
            }

            .select-product {
                border-top: 1px solid $sn-hr-light;
                padding-top: 20px;


                .btn-select-product {
                    box-shadow: 0 3px 6px 0 $add-cart-shadow;
                    width: 100%;
                    padding: 18px 0;
                    font-size: 1.6rem;
                    margin: 10px 0;
                }
            }
        }

        .free-shipping {
            color: $add-cart-color;
            font-size: 1.6rem !important;
            font-weight: normal;
            margin: 0;
        }

        .product-quantity {
            border-top: 1px solid $sn-hr-light;
            padding-top: 20px;
            padding-bottom: 20px;

            .label {
                font-family: OpenSans;
                font-size: 1.6rem !important;
                font-weight: normal;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.37;
                letter-spacing: 0.4px;
                text-align: left;
                color: #454545;
                padding-right: 10px;
            }

            h2 {
                margin: 0;
            }
        }

        .number-btn {
            display: inline-flex;
            font-size: 1.6rem;
            white-space: nowrap;
            user-select: none;
            margin: 0 auto;

            .manage-input, .amount {
                box-sizing: border-box;
                border: 1px solid #cccccc;
                width: 35px;
                height: 35px;
                display: inline-flex;
                vertical-align: middle;
                font-size: 2rem;
                align-items: center;
                justify-content: center;
            }

            .manage-input {
              cursor: pointer;
              color: #6c6c6c;
              font-size: 1.2rem;
              cursor: pointer;
              align-items: center;
              justify-content: center;

              &:hover {
                background: #ddd4cf;
              }

              &.disabled {
                cursor: not-allowed;
                opacity: .5;

                &:hover {
                  background-color: initial;
                }
              }
            }

            .amount {
              cursor: text;
              font-size: 1.6rem;
              color: #212121;
              border-right: none;
              border-left: none;
              padding: 0;
              text-align: center;

              /* Chrome, Safari, Edge, Opera */
              &::-webkit-outer-spin-button,
              &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }

              /* Firefox */
              &[type=number] {
                -moz-appearance: textfield;
              }

              &:disabled {
                cursor: not-allowed;
                opacity: .5;
              }
            }
          }

        @media only screen and (max-width: 992px) {
            grid-template-columns: 1fr;
            grid-column-gap: 0;
            padding-top: 50px;

            .product-images-gallery {
                grid-row: 1;
                grid-auto-rows: min-content;
            }

            .product-manage {
                display: grid;
                grid-row-gap: 20px;
                grid-row: 2;
                grid-auto-rows: min-content;
            }
        }
    }

</style>
